import { Helmet } from 'react-helmet-async';

type JsonLdData = Record<string, unknown>;

interface JsonLdProps {
  data: JsonLdData | JsonLdData[];
}

export default function JsonLd({ data }: JsonLdProps) {
  const items = Array.isArray(data) ? data : [data];
  return (
    <Helmet>
      {items.map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}

// Reusable schema builders
export const organizationSchema: JsonLdData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Romy',
  url: 'https://romyhealth.co',
  logo: 'https://romyhealth.co/wordmark.png',
  description:
    'AI-powered women\'s health companion for endometriosis, PCOS, PMDD, fertility, and more.',
  sameAs: [],
};

export const webApplicationSchema: JsonLdData = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Romy',
  url: 'https://romyhealth.co',
  applicationCategory: 'HealthApplication',
  operatingSystem: 'Web, iOS, Android',
  description:
    'AI-powered women\'s health companion. Track symptoms, get personalized insights, prepare for doctor visits, and connect with a supportive community.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
};

export function faqSchema(
  items: { question: string; answer: string }[],
): JsonLdData {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export function medicalConditionSchema(condition: {
  name: string;
  description: string;
  symptoms: string[];
  url: string;
}): JsonLdData {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalCondition',
    name: condition.name,
    description: condition.description,
    signOrSymptom: condition.symptoms.map((s) => ({
      '@type': 'MedicalSignOrSymptom',
      name: s,
    })),
    url: condition.url,
  };
}

export function breadcrumbSchema(
  items: { name: string; url: string }[],
): JsonLdData {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function articleSchema(article: {
  title: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
}): JsonLdData {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: article.title,
    description: article.description,
    url: article.url,
    ...(article.datePublished && { datePublished: article.datePublished }),
    ...(article.dateModified && { dateModified: article.dateModified }),
    publisher: organizationSchema,
    isAccessibleForFree: true,
  };
}

export function softwareAppSchema(tool: {
  name: string;
  description: string;
  url: string;
}): JsonLdData {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: tool.name,
    description: tool.description,
    url: tool.url,
    applicationCategory: 'HealthApplication',
    operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };
}

export function collectionPageSchema(page: {
  name: string;
  description: string;
  url: string;
}): JsonLdData {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: page.name,
    description: page.description,
    url: page.url,
    publisher: organizationSchema,
  };
}
